import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchForecast,
  refreshForecast,
  InventoryApiError,
} from '../../services/inventoryApi';
import './DemandForecast.css';

interface DemandForecastProps {
  selectedStore: string;
  enterpriseId: string;
}

interface ForecastRow {
  ingredientId: string;
  name: string;
  unit?: string;
  daily: number[]; // 14 values
}

interface ForecastResponse {
  storeId: string;
  horizonDays?: number;
  modelSource?: 'sagemaker' | 'bedrock' | 'unconfigured' | string;
  generatedAt?: string;
  forecasts?: ForecastRow[];
  data?: ForecastRow[];
}

const DAYS = 14;

const DemandForecast: React.FC<DemandForecastProps> = ({ selectedStore }) => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<ForecastResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!selectedStore) return;
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const res = await fetchForecast(selectedStore);
      setData(res);
    } catch (e) {
      if (e instanceof InventoryApiError) {
        setError(e.message);
        setErrorCode(e.code || String(e.status));
      } else {
        setError('Failed to load forecast');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedStore]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = async () => {
    if (!selectedStore) return;
    setRefreshing(true);
    setError(null);
    setErrorCode(null);
    try {
      await refreshForecast(selectedStore);
      await load();
    } catch (e) {
      if (e instanceof InventoryApiError) {
        setError(e.message);
        setErrorCode(e.code || String(e.status));
      } else {
        setError('Failed to refresh forecast');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const rows: ForecastRow[] = data?.forecasts || data?.data || [];
  const modelSource = data?.modelSource || 'unconfigured';
  const isNotConfigured =
    errorCode === 'FORECAST_MODEL_NOT_CONFIGURED' ||
    modelSource === 'unconfigured';

  return (
    <div className="demand-forecast">
      <div className="demand-forecast__header">
        <div>
          <h2>Demand Forecast</h2>
          <p className="demand-forecast__sub">
            14-day projected consumption per ingredient
          </p>
        </div>
        <div className="demand-forecast__header-actions">
          <span className={`demand-forecast__badge demand-forecast__badge--${modelSource}`}>
            {modelSource}
          </span>
          <button
            className="btn-modern btn-modern-accent"
            onClick={handleRefresh}
            disabled={refreshing || loading || !selectedStore}
          >
            {refreshing ? 'Refreshing...' : 'Refresh forecast'}
          </button>
        </div>
      </div>

      {loading && (
        <div className="demand-forecast__state">Loading forecast...</div>
      )}

      {!loading && isNotConfigured && (
        <div className="demand-forecast__empty">
          <h3>Forecast model not configured</h3>
          <p>
            Contact your platform admin to provision a SageMaker or Bedrock
            endpoint. Once configured, 14-day demand predictions will appear here.
          </p>
        </div>
      )}

      {!loading && !isNotConfigured && error && (
        <div className="demand-forecast__error">{error}</div>
      )}

      {!loading && !isNotConfigured && !error && rows.length === 0 && (
        <div className="demand-forecast__state">
          No forecast data yet. Click "Refresh forecast" to generate predictions.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="demand-forecast__grid-wrap">
          <table className="demand-forecast__grid">
            <thead>
              <tr>
                <th>Ingredient</th>
                {Array.from({ length: DAYS }).map((_, i) => (
                  <th key={i}>D+{i + 1}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ingredientId}>
                  <td className="demand-forecast__ingredient">
                    <strong>{r.name}</strong>
                    {r.unit && <span className="demand-forecast__unit"> ({r.unit})</span>}
                  </td>
                  {Array.from({ length: DAYS }).map((_, i) => {
                    const val = r.daily?.[i];
                    return (
                      <td key={i} className="demand-forecast__cell">
                        {val == null ? '-' : Number(val).toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DemandForecast;
